import { Flex, Group, IconFont, SegmentedControl, Text } from '@byecode/ui'
import { ListItem4ByecodeUi, useAtomData } from '@lighthouse/shared'
import React, { useCallback } from 'react'
import { Controller, useFormContext, useWatch } from 'react-hook-form'
import styled from 'styled-components'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { InheritLabel } from '@/components/InheritLabel'
import { PopoverQuestion } from '@/components/PopoverQuestion'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import { OVERFLOW_OPTIONS } from '../constants'
import { useSetExtendsKeys } from '../hooks/useMouseEventDistance'
import { MinMaxRectSizeAdder } from './MinMaxRectSizeAdder'
import { RectMinMaxSizeInput } from './RectMinMaxSizeInput'
import type { VariableOptions } from './RectSizeInput'
import { RectSizeInput } from './RectSizeInput'

const SCxIcon = styled(IconFont)`
    color: var(--color-gray-400);
    &:hover {
        color: var(--color-black);
    }
`

export interface SizeSettingSwitch {
    hideHeight?: boolean
    hideMinMaxRectSizeAdder?: boolean
    hideMaxWidth?: boolean
    hideMinWidth?: boolean
    hideMaxHeight?: boolean
    hideMinHeight?: boolean
    hideOverflow?: boolean

    disableWidthAuto?: boolean
    disableHeightAuto?: boolean

    disableWidth?: boolean
    disableMinWidth?: boolean
    disableMaxWidth?: boolean
    disableHeight?: boolean
    disableMinHeight?: boolean
    disableMaxHeight?: boolean
    disableOverflow?: boolean
}

interface SizeSettingProps extends SizeSettingSwitch {}

export const SizeSetting: React.FC<SizeSettingProps> = ({
    hideHeight,
    hideMinMaxRectSizeAdder,
    hideMaxWidth,
    hideMinWidth,
    hideMaxHeight,
    hideMinHeight,
    hideOverflow,
    disableWidthAuto,
    disableHeightAuto,
    disableWidth,
    disableMinWidth,
    disableMaxWidth,
    disableHeight,
    disableMinHeight,
    disableMaxHeight,
    disableOverflow
}) => {
    const { control } = useFormContext()
    const { handleSetBreakKey, handleRemoveBreakKey } = useSetExtendsKeys()
    const [width, height, minWidth, maxWidth, minHeight, maxHeight] = useWatch({
        control,
        name: [
            'breakPoint.size.width',
            'breakPoint.size.height',
            'breakPoint.size.minWidth',
            'breakPoint.size.maxWidth',
            'breakPoint.size.minHeight',
            'breakPoint.size.maxHeight'
        ]
    })

    const getUnitLabel = (val?: { size?: unknown; unit?: string }) => {
        if (!val?.unit || val.size === 'auto' || val.size === 'fill') return ''
        return ` (${val.unit})`
    }

    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })
    const userOption = useGetUserRecordOptions()
    const { customViewOption } = useVariableCustomViewOption()

    const variableOptions: VariableOptions = {
        viewOptions: customViewOption ? [customViewOption] : undefined,
        pageOptions,
        userOption,
        dataSourceList
    }

    return (
        <Group
            label={
                <PopoverQuestion
                    type="size"
                    arrowOffset={10}
                    renderTarget={(node, ref) => (
                        <Flex alignItems="center" gap={4} ref={ref}>
                            <Text>尺寸</Text>
                            {node}
                        </Flex>
                    )}
                />
            }
            extra={
                !hideMinMaxRectSizeAdder && (
                    <MinMaxRectSizeAdder hideMinWidth={hideMinWidth} hideMaxHeight={hideMaxHeight} hideMinHeight={hideMinHeight} />
                )
            }
        >
            <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                <InheritLabel label={`宽度${getUnitLabel(width)}`} name="breakPoint.size.width" />
                <div style={{ display: 'flex' }}>
                    <Controller
                        control={control}
                        name="breakPoint.size.width"
                        render={({ field }) => (
                            <RectSizeInput
                                type="width"
                                disableAutoOption={disableWidthAuto}
                                disabled={disableWidth}
                                value={field.value}
                                variableOptions={variableOptions}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.size.width')
                                }}
                            />
                        )}
                    />
                </div>
            </ListItem4ByecodeUi>
            {!hideMinWidth && minWidth && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={`最小宽度${getUnitLabel(minWidth)}`} name="breakPoint.size.minWidth" />
                    <div style={{ display: 'flex' }}>
                        <Controller
                            control={control}
                            name="breakPoint.size.minWidth"
                            render={({ field }) => (
                                <RectMinMaxSizeInput
                                    type="minWidth"
                                    disable={disableMinWidth}
                                    value={field.value}
                                    variableOptions={variableOptions}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.size.minWidth')
                                    }}
                                    onRemove={() => {
                                        field.onChange(undefined)
                                        handleSetBreakKey('breakPoint.size.minWidth')
                                    }}
                                />
                            )}
                        />
                    </div>
                </ListItem4ByecodeUi>
            )}
            {!hideMaxWidth && maxWidth && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={`最大宽度${getUnitLabel(maxWidth)}`} name="breakPoint.size.maxWidth" />
                    <div style={{ display: 'flex' }}>
                        <Controller
                            control={control}
                            name="breakPoint.size.maxWidth"
                            render={({ field }) => (
                                <RectMinMaxSizeInput
                                    type="maxWidth"
                                    value={field.value}
                                    disable={disableMaxWidth}
                                    variableOptions={variableOptions}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.size.maxWidth')
                                    }}
                                    onRemove={() => {
                                        field.onChange(undefined)
                                        handleSetBreakKey('breakPoint.size.maxWidth')
                                    }}
                                />
                            )}
                        />
                    </div>
                </ListItem4ByecodeUi>
            )}
            {!hideHeight && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={`高度${getUnitLabel(height)}`} name="breakPoint.size.height" />
                    <Controller
                        control={control}
                        name="breakPoint.size.height"
                        render={({ field }) => (
                            <RectSizeInput
                                type="height"
                                disabled={disableHeight}
                                disableAutoOption={disableHeightAuto}
                                value={field.value}
                                variableOptions={variableOptions}
                                onChange={val => {
                                    handleSetBreakKey('breakPoint.size.height')
                                    field.onChange(val)
                                }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}
            {!hideMinHeight && minHeight && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={`最小高度${getUnitLabel(minHeight)}`} name="breakPoint.size.minHeight" />
                    <div style={{ display: 'flex' }}>
                        <Controller
                            control={control}
                            name="breakPoint.size.minHeight"
                            render={({ field }) => (
                                <RectMinMaxSizeInput
                                    type="minHeight"
                                    value={field.value}
                                    disable={disableMinHeight}
                                    variableOptions={variableOptions}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.size.minHeight')
                                    }}
                                    onRemove={() => {
                                        field.onChange(undefined)
                                        handleSetBreakKey('breakPoint.size.minHeight')
                                    }}
                                />
                            )}
                        />
                    </div>
                </ListItem4ByecodeUi>
            )}
            {!hideMaxHeight && maxHeight && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label={`最大高度${getUnitLabel(maxHeight)}`} name="breakPoint.size.maxHeight" />
                    <div style={{ display: 'flex' }}>
                        <Controller
                            control={control}
                            name="breakPoint.size.maxHeight"
                            render={({ field }) => (
                                <RectMinMaxSizeInput
                                    type="maxHeight"
                                    value={field.value}
                                    disable={disableMaxHeight}
                                    variableOptions={variableOptions}
                                    onChange={val => {
                                        field.onChange(val)
                                        handleSetBreakKey('breakPoint.size.maxHeight')
                                    }}
                                    onRemove={() => {
                                        field.onChange(undefined)
                                        handleSetBreakKey('breakPoint.size.maxHeight')
                                    }}
                                />
                            )}
                        />
                    </div>
                </ListItem4ByecodeUi>
            )}
            {!hideOverflow && (
                <ListItem4ByecodeUi justifyContent="space-between" alignItems="center">
                    <InheritLabel label="当内容超出" name="breakPoint.size.overflow" />
                    <Controller
                        control={control}
                        name="breakPoint.size.overflow"
                        render={({ field }) => (
                            <SegmentedControl
                                value={field.value}
                                onChange={val => {
                                    field.onChange(val)
                                    handleSetBreakKey('breakPoint.size.overflow')
                                }}
                                disabled={disableOverflow}
                                fullWidth
                                data={OVERFLOW_OPTIONS}
                                style={{ width: 180 }}
                            />
                        )}
                    />
                </ListItem4ByecodeUi>
            )}
        </Group>
    )
}
